/**
 * editCommandPipeline — ScheduleDraft 编辑命令到 ChangeSet 的纯函数管道。
 *
 * 本模块从 Workspace Context 下沉 ChangeSet 构建、字段派生和 GT 合并逻辑，
 * 让 Provider 只负责依赖注入，保持 Pipeline 的 UI/React 生命周期无关边界。
 * ChangeSet 构建分为后端驱动、前端批量预计算和单航班规则派生三条路径。
 *
 * 设计文档 → docs/30_editing_publish/30_local_edit_and_undo.md
 * 骨架关联 → hooks/useScheduleDraftEditPipeline.js (命令执行序列), context/ScheduleDraftEditorWorkspaceContext.jsx (依赖注入), lib/flightRecalculationRules.js (字段派生), lib/flightTimingUtils.js (GT 派生)
 */

import {
    ADD_FLIGHT,
    AUTO_ADJUST_CURFEW,
    AUTO_ADJUST_TURNAROUND,
    AUTO_CALCULATE_FLIGHTS,
    BATCH_ADD_FLIGHTS,
    BATCH_CHANGE_AIRPORT,
    BATCH_DELETE_FLIGHTS,
    BATCH_SWITCH_LINE,
    BATCH_SHIFT,
    BATCH_UPDATE_FLIGHT_FIELDS,
    DELETE_FLIGHT,
} from "./editCommand";
import { normalizeStcTypeEmptyValue } from "../constants/scheduleDraftOptions";
import { applyRecalculationRules } from "./flightRecalculationRules";
import { calculateDateTimeDiffMinutes, getIsoDay, mergeCalculatedGroundTimeChanges, normalizeTimeValue, valuesEqual } from "./flightTimingUtils";
import { routesByIdToObject } from "./routeUtils";

const BACKEND_DRIVEN_TYPES = new Set([
    AUTO_CALCULATE_FLIGHTS,
    AUTO_ADJUST_CURFEW,
    AUTO_ADJUST_TURNAROUND,
]);

const FRONTEND_BATCH_TYPES = new Set([
    BATCH_CHANGE_AIRPORT,
    BATCH_SWITCH_LINE,
    BATCH_SHIFT,
]);

// Resolve leg auto-match before explicit route/block/time edits so direct user fields win.
const USER_FIELD_PROCESSING_ORDER = ["departure_airport", "arrival_airport", "flight_route_id", "block_time_minutes", "std", "sta"];
const USER_FIELD_PROCESSING_ORDER_SET = new Set(USER_FIELD_PROCESSING_ORDER);

const USER_CHANGE_SOURCE = "USER";
const ADDED_CHANGE_STATUS = "ADDED";
const DELETED_CHANGE_STATUS = "DELETED";
const DISPLAY_ORDER_STEP = 1024;
const ROUTE_DERIVED_PAYLOAD_FIELDS = new Set([
    "block_time_minutes",
    "min_block_time_minutes",
    "sta",
    "route_snapshot",
    "flight_route_name",
    "flight_route_block_time_minutes",
]);

function buildChangeSetId(prefix = "cs") {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function mergeRouteArrays(...routeArrays) {
    const order = [];
    const routesByKey = new Map();

    routeArrays.flatMap((routes) => (Array.isArray(routes) ? routes : [])).filter(Boolean).forEach((route, index) => {
        const routeValue = route?.route || route;
        const key = routeValue?.id == null ? `__route_index_${index}` : String(routeValue.id);

        if (!routesByKey.has(key)) {
            order.push(key);
        }
        routesByKey.set(key, route);
    });

    return order.map((key) => routesByKey.get(key));
}

function routesByLegToObject(routesByLeg) {
    if (routesByLeg instanceof Map) {
        return Object.fromEntries(routesByLeg.entries());
    }

    if (routesByLeg && typeof routesByLeg === "object" && !Array.isArray(routesByLeg)) {
        return routesByLeg;
    }

    return {};
}

function mergeCommandReferenceData(referenceData = {}, commandReferenceData = {}) {
    const hasRouteArrays = Array.isArray(referenceData.routes) || Array.isArray(commandReferenceData.routes);
    const mergedReferenceData = {
        ...referenceData,
        ...commandReferenceData,
        routesById: {
            ...routesByIdToObject(referenceData.routesById),
            ...routesByIdToObject(commandReferenceData.routesById),
        },
        routesByLeg: {
            ...routesByLegToObject(referenceData.routesByLeg),
            ...routesByLegToObject(commandReferenceData.routesByLeg),
        },
    };

    if (hasRouteArrays) {
        mergedReferenceData.routes = mergeRouteArrays(referenceData.routes, commandReferenceData.routes);
    }

    return mergedReferenceData;
}

function buildBackendDrivenChangeSet(command, localFlightsMap, airportConstraintsMap) {
    const { payload, type } = command;
    const changeSet = payload?.changeSet;
    const changes = mergeCalculatedGroundTimeChanges(changeSet?.changes || [], localFlightsMap, airportConstraintsMap);

    return {
        command,
        changeSet: {
            id: changeSet?.id || buildChangeSetId("auto-calc"),
            type,
            label: changeSet?.label || "自动计算航班时间",
            timestamp: changeSet?.timestamp || Date.now(),
            changes,
        },
        warnings: [],
    };
}

function isValidFrontendBatchChange(change) {
    return Boolean(
        change
            && typeof change === "object"
            && change.flightId !== undefined
            && change.flightId !== null
            && change.field !== undefined
            && change.field !== null
            && Object.prototype.hasOwnProperty.call(change, "before")
            && change.before !== undefined
            && Object.prototype.hasOwnProperty.call(change, "after")
            && change.after !== undefined,
    );
}

function buildFrontendBatchChangeSet(command, localFlightsMap, airportConstraintsMap) {
    const { payload, type } = command;
    const changeSet = payload?.changeSet;
    const warnings = [];
    const rawChanges = changeSet?.changes || [];
    const changesToValidate = Array.isArray(rawChanges) ? rawChanges : [];

    if (!Array.isArray(rawChanges)) {
        warnings.push("忽略非法批量变更列表: changes 必须是数组");
    }

    const validChanges = changesToValidate.filter((change, index) => {
        if (isValidFrontendBatchChange(change)) {
            return true;
        }

        warnings.push(`忽略非法批量变更 #${index + 1}: 缺少 flightId、field、before 或 after`);
        return false;
    });
    const changes = mergeCalculatedGroundTimeChanges(validChanges, localFlightsMap, airportConstraintsMap);

    return {
        command,
        changeSet: {
            id: changeSet?.id || buildChangeSetId("auto-calc"),
            type,
            label: changeSet?.label || "自动计算航班时间",
            timestamp: changeSet?.timestamp || Date.now(),
            changes,
        },
        warnings,
    };
}

function buildFlightNotFoundChangeSet(command) {
    return {
        command,
        changeSet: {
            id: buildChangeSetId(),
            type: command.type,
            label: `${command.type} (flight not found)`,
            timestamp: Date.now(),
            changes: [],
        },
        warnings: ["目标航班不在本地状态中"],
    };
}

function normalizeDateTimeValue(value) {
    return normalizeTimeValue(value);
}

function formatFlightDate(value) {
    const normalizedValue = normalizeDateTimeValue(value);
    return normalizedValue && /^\d{4}-\d{2}-\d{2}T/.test(String(normalizedValue)) ? String(normalizedValue).slice(0, 10) : undefined;
}

function stripLocalOnlyPayloadFields(payload = {}) {
    return Object.entries(payload || {}).reduce((fields, [field, value]) => {
        if (!field.startsWith("_") && field !== "trackId") {
            fields[field] = value;
        }
        return fields;
    }, {});
}

function normalizeEditPayloadValue(field, value) {
    if (field === "stc_type") {
        return normalizeStcTypeEmptyValue(value);
    }
    return value;
}

function normalizeEditPayload(payload = {}) {
    return Object.entries(payload || {}).reduce((fields, [field, value]) => {
        fields[field] = normalizeEditPayloadValue(field, value);
        return fields;
    }, {});
}

function getFlightId(flight) {
    return flight?.id ?? flight?.flight_id;
}

function getFlightTrackId(flight) {
    return flight?.track_id ?? flight?.trackId;
}

function isDeletedFlight(flight) {
    return String(flight?.change_status || "").toUpperCase() === DELETED_CHANGE_STATUS;
}

function getInsertionContext(meta = {}) {
    return meta.insertionContext || meta.insertion || null;
}

function getInsertionAnchorId(insertionContext = {}) {
    const context = insertionContext || {};
    return context.anchorFlightId
        ?? context.anchor_flight_id
        ?? getFlightId(context.anchorFlight)
        ?? getFlightId(context.anchor_flight);
}

function getTrackOrderedFlights(localFlightsMap, trackId, addedFlightId) {
    if (!localFlightsMap || !trackId) {
        return [];
    }

    return Array.from(localFlightsMap.values())
        .filter((flight) => String(getFlightTrackId(flight) || "") === String(trackId))
        .filter((flight) => String(getFlightId(flight) || "") !== String(addedFlightId))
        .filter((flight) => !isDeletedFlight(flight));
}

function getNextDisplayOrder(flights = []) {
    return flights.reduce((maxOrder, flight) => {
        const displayOrder = Number(flight?.display_order);
        return Number.isFinite(displayOrder) ? Math.max(maxOrder, displayOrder) : maxOrder;
    }, 0) + DISPLAY_ORDER_STEP;
}

function getDisplayOrderAtPosition(flight, index) {
    const displayOrder = Number(flight?.display_order);
    if (!Number.isFinite(displayOrder)) {
        throw new Error(`航班 ${getFlightId(flight) || index} 缺少 display_order`);
    }
    return displayOrder;
}

function sparseDisplayOrderBetween(previousOrder, nextOrder) {
    if (previousOrder == null && nextOrder == null) {
        return DISPLAY_ORDER_STEP;
    }
    if (previousOrder == null) {
        return nextOrder - DISPLAY_ORDER_STEP;
    }
    if (nextOrder == null) {
        return previousOrder + DISPLAY_ORDER_STEP;
    }
    if (nextOrder - previousOrder > 1) {
        return Math.floor((previousOrder + nextOrder) / 2);
    }

    throw new Error("display_order 稀疏间隔已耗尽，请先执行结构顺序重排");
}

function assignSparseDisplayOrderAtIndex(addedFlight, orderedFlights, insertIndex) {
    const previousFlight = insertIndex > 0 ? orderedFlights[insertIndex - 1] : null;
    const nextFlight = insertIndex < orderedFlights.length ? orderedFlights[insertIndex] : null;
    const previousOrder = previousFlight ? getDisplayOrderAtPosition(previousFlight, insertIndex - 1) : null;
    const nextOrder = nextFlight ? getDisplayOrderAtPosition(nextFlight, insertIndex) : null;

    addedFlight.display_order = sparseDisplayOrderBetween(previousOrder, nextOrder);
    return [];
}

function assignAddFlightDisplayOrder(addedFlight, localFlightsMap, meta = {}) {
    const trackId = getFlightTrackId(addedFlight);
    const addedFlightId = getFlightId(addedFlight);
    const orderedFlights = getTrackOrderedFlights(localFlightsMap, trackId, addedFlightId);
    const insertionContext = getInsertionContext(meta);
    const mode = insertionContext?.mode;
    const anchorId = getInsertionAnchorId(insertionContext);

    if ((mode === "before" || mode === "after") && anchorId != null) {
        const anchorIndex = orderedFlights.findIndex((flight) => String(getFlightId(flight)) === String(anchorId));
        if (anchorIndex >= 0) {
            return assignSparseDisplayOrderAtIndex(addedFlight, orderedFlights, mode === "before" ? anchorIndex : anchorIndex + 1);
        }
    }

    if (addedFlight.display_order == null) {
        addedFlight.display_order = getNextDisplayOrder(orderedFlights);
    }
    return [];
}

function buildAddedFlight(command) {
    const payload = normalizeEditPayload(stripLocalOnlyPayloadFields(command.payload || {}));
    const flightId = command.flightId ?? payload.id;
    const std = normalizeDateTimeValue(payload.std);
    const sta = normalizeDateTimeValue(payload.sta);
    const flightDate = payload.flight_date || formatFlightDate(std);
    const blockTimeMinutes = payload.block_time_minutes ?? calculateDateTimeDiffMinutes(std, sta, { clampToZero: true });

    return {
        ...payload,
        id: flightId,
        track_id: payload.track_id || command.meta?.trackId || command.meta?.track_id,
        std,
        sta,
        flight_date: flightDate,
        day_of_week: payload.day_of_week || (std ? getIsoDay(std) : undefined),
        block_time_minutes: blockTimeMinutes,
        is_fixed: Boolean(payload.is_fixed),
        change_status: ADDED_CHANGE_STATUS,
        change_source: payload.change_source || USER_CHANGE_SOURCE,
    };
}

function buildAddFlightChangeSet(command, localFlightsMap) {
    const addedFlight = buildAddedFlight(command);
    const displayOrderChanges = assignAddFlightDisplayOrder(addedFlight, localFlightsMap, command.meta);
    const flightId = addedFlight.id;
    const insertionContext = getInsertionContext(command.meta);
    const insertionAnchorId = getInsertionAnchorId(insertionContext);

    if (!flightId) {
        return buildFlightNotFoundChangeSet(command);
    }

    const changes = Object.entries(addedFlight)
        .filter(([, value]) => value !== undefined)
        .map(([field, value]) => ({
            flightId: String(flightId),
            field,
            before: undefined,
            after: value,
        }));
    changes.push(...displayOrderChanges);
    return {
        command,
        changeSet: {
            id: buildChangeSetId("add-flight"),
            type: ADD_FLIGHT,
            label: `新增 ${addedFlight.flight_number || flightId}`,
            timestamp: Date.now(),
            meta: {
                insertionContext: {
                    mode: insertionContext?.mode || "end",
                    anchorFlightId: insertionAnchorId ?? null,
                    trackId: addedFlight.track_id ?? null,
                },
            },
            changes,
        },
        warnings: [],
    };
}

function buildBatchAddFlightsChangeSet(command, localFlightsMap) {
    const rawFlights = Array.isArray(command?.payload?.flights) ? command.payload.flights : [];
    const workingFlightsMap = new Map(localFlightsMap || []);
    const changes = [];
    const addedFlightIds = [];
    const warnings = [];

    rawFlights.forEach((flightPayload, index) => {
        const payload = flightPayload || {};
        const flightId = payload.id ?? payload.flightId ?? payload.flight_id;
        if (!flightId) {
            warnings.push(`忽略第 ${index + 1} 个复制航班：缺少本地航班 ID`);
            return;
        }

        const trackId = payload.track_id || payload.trackId || command.meta?.trackId || command.meta?.track_id;
        const subCommand = {
            ...command,
            flightId,
            payload: {
                ...payload,
                id: flightId,
                track_id: trackId,
            },
            meta: {
                ...(command.meta || {}),
                trackId,
            },
        };
        const addedFlight = buildAddedFlight(subCommand);
        const displayOrderChanges = assignAddFlightDisplayOrder(addedFlight, workingFlightsMap, subCommand.meta);
        const addedFlightId = getFlightId(addedFlight);
        if (!addedFlightId) {
            warnings.push(`忽略第 ${index + 1} 个复制航班：无法生成本地航班 ID`);
            return;
        }

        addedFlightIds.push(String(addedFlightId));
        changes.push(...Object.entries(addedFlight)
            .filter(([, value]) => value !== undefined)
            .map(([field, value]) => ({
                flightId: String(addedFlightId),
                field,
                before: undefined,
                after: value,
            })));
        changes.push(...displayOrderChanges);
        workingFlightsMap.set(String(addedFlightId), addedFlight);
    });

    return {
        command,
        changeSet: {
            id: buildChangeSetId("batch-add-flights"),
            type: BATCH_ADD_FLIGHTS,
            label: command?.payload?.label || `复制 ${addedFlightIds.length} 个航班`,
            timestamp: Date.now(),
            meta: {
                addedFlightIds,
            },
            changes,
        },
        warnings,
    };
}

function buildDeleteFlightChangeSet(command, localFlightsMap) {
    const flightIdStr = String(command.flightId);
    const currentFlight = localFlightsMap.get(flightIdStr);
    if (!currentFlight) {
        return buildFlightNotFoundChangeSet(command);
    }

    if (String(currentFlight.change_status || "").toUpperCase() === DELETED_CHANGE_STATUS) {
        return {
            command,
            changeSet: {
                id: buildChangeSetId("delete-flight"),
                type: DELETE_FLIGHT,
                label: `删除 ${currentFlight.flight_number || flightIdStr}`,
                timestamp: Date.now(),
                changes: [],
            },
            warnings: ["目标航班已标记删除"],
        };
    }

    return {
        command,
        changeSet: {
            id: buildChangeSetId("delete-flight"),
            type: DELETE_FLIGHT,
            label: `删除 ${currentFlight.flight_number || flightIdStr}`,
            timestamp: Date.now(),
            changes: [
                {
                    flightId: flightIdStr,
                    field: "change_status",
                    before: currentFlight.change_status ?? "UNCHANGED",
                    after: DELETED_CHANGE_STATUS,
                },
            ],
        },
        warnings: [],
    };
}

function orderUserChangedFields(fields = []) {
    const fieldSet = new Set(fields);
    return [
        ...USER_FIELD_PROCESSING_ORDER.filter((field) => fieldSet.has(field)),
        ...fields.filter((field) => !USER_FIELD_PROCESSING_ORDER_SET.has(field)),
    ];
}

function hasOwn(object, key) {
    return Object.prototype.hasOwnProperty.call(object || {}, key);
}

function stripRouteDerivedPayloadFields(payload = {}, currentFlight = {}) {
    if (!hasOwn(payload, "flight_route_id") || valuesEqual("flight_route_id", currentFlight?.flight_route_id, payload.flight_route_id)) {
        return payload;
    }

    return Object.entries(payload).reduce((fields, [field, value]) => {
        if (!ROUTE_DERIVED_PAYLOAD_FIELDS.has(field)) {
            fields[field] = value;
        }
        return fields;
    }, {});
}

function normalizeBatchUpdateItems(payload = {}) {
    return (Array.isArray(payload?.updates) ? payload.updates : [])
        .map((item) => {
            const flightId = item?.flightId ?? item?.flight_id ?? item?.id;
            const fields = item?.payload || item?.fields || item?.values || {};
            return {
                flightId: flightId == null ? null : String(flightId),
                payload: stripLocalOnlyPayloadFields(fields),
            };
        })
        .filter((item) => item.flightId && item.payload && typeof item.payload === "object" && !Array.isArray(item.payload));
}

function buildBatchUpdateFlightFieldsChangeSet(command, localFlightsMap, referenceData = {}, airportConstraintsMap = {}) {
    const updates = normalizeBatchUpdateItems(command.payload);
    const changes = [];
    const allWarnings = [];
    const effectiveReferenceData = mergeCommandReferenceData(referenceData, command.meta?.referenceData || {});

    updates.forEach((update) => {
        const currentFlight = localFlightsMap.get(String(update.flightId));
        if (!currentFlight) {
            allWarnings.push(`目标航班 ${update.flightId} 不在本地状态中`);
            return;
        }

        const payload = normalizeEditPayload(stripRouteDerivedPayloadFields(update.payload, currentFlight));
        const allPatches = {};
        const userChangedFields = Object.keys(payload).filter((field) => !valuesEqual(field, currentFlight[field], payload[field]));
        const fieldsToProcess = orderUserChangedFields(userChangedFields);

        fieldsToProcess.forEach((field) => {
            const newValue = payload[field];
            const oldValue = currentFlight[field];
            changes.push({
                flightId: String(update.flightId),
                field,
                before: oldValue ?? null,
                after: newValue ?? null,
            });

            const derivedFlight = {
                ...currentFlight,
                ...allPatches,
                ...payload,
                [field]: newValue,
            };
            const { patches, warnings } = applyRecalculationRules(field, {
                newValue,
                previousValue: oldValue,
                currentFlight: derivedFlight,
                referenceData: effectiveReferenceData,
            });
            Object.assign(allPatches, patches);
            allWarnings.push(...warnings);
        });

        Object.entries(allPatches).forEach(([field, afterValue]) => {
            const alreadyChanged = changes.some((change) => change.flightId === String(update.flightId) && change.field === field);
            if (!alreadyChanged && afterValue !== undefined && !valuesEqual(field, currentFlight[field], afterValue)) {
                changes.push({
                    flightId: String(update.flightId),
                    field,
                    before: currentFlight[field] ?? null,
                    after: afterValue ?? null,
                });
            }
        });

        const sourceAlreadyChanged = changes.some((change) => change.flightId === String(update.flightId) && change.field === "change_source");
        if (userChangedFields.length > 0 && !sourceAlreadyChanged && !valuesEqual("change_source", currentFlight.change_source, USER_CHANGE_SOURCE)) {
            changes.push({
                flightId: String(update.flightId),
                field: "change_source",
                before: currentFlight.change_source ?? null,
                after: USER_CHANGE_SOURCE,
            });
        }
    });

    const affectedFlightCount = new Set(changes.map((change) => change.flightId)).size;
    const fieldLabels = Array.from(new Set(updates.flatMap((update) => Object.keys(update.payload || {})))).join(", ");
    return {
        command,
        changeSet: {
            id: buildChangeSetId("batch-update"),
            type: BATCH_UPDATE_FLIGHT_FIELDS,
            label: command?.payload?.label || `批量修改 ${affectedFlightCount || updates.length} 个航班${fieldLabels ? ` ${fieldLabels}` : ""}`,
            timestamp: Date.now(),
            changes: mergeCalculatedGroundTimeChanges(changes, localFlightsMap, airportConstraintsMap),
        },
        warnings: allWarnings,
    };
}

function getBatchDeleteFlightIds(payload = {}) {
    return Array.from(new Set((Array.isArray(payload?.flightIds) ? payload.flightIds : payload?.flight_ids || [])
        .map((flightId) => (flightId == null ? null : String(flightId)))
        .filter(Boolean)));
}

function buildBatchDeleteFlightsChangeSet(command, localFlightsMap) {
    const flightIds = getBatchDeleteFlightIds(command.payload);
    const changes = [];
    const warnings = [];

    flightIds.forEach((flightId) => {
        const currentFlight = localFlightsMap.get(String(flightId));
        if (!currentFlight) {
            warnings.push(`目标航班 ${flightId} 不在本地状态中`);
            return;
        }
        if (String(currentFlight.change_status || "").toUpperCase() === DELETED_CHANGE_STATUS) {
            warnings.push(`航班 ${currentFlight.flight_number || flightId} 已标记删除`);
            return;
        }

        changes.push({
            flightId: String(flightId),
            field: "change_status",
            before: currentFlight.change_status ?? "UNCHANGED",
            after: DELETED_CHANGE_STATUS,
        });
    });

    const affectedFlightCount = new Set(changes.map((change) => change.flightId)).size;
    return {
        command,
        changeSet: {
            id: buildChangeSetId("batch-delete-flights"),
            type: BATCH_DELETE_FLIGHTS,
            label: command?.payload?.label || `批量删除 ${affectedFlightCount || flightIds.length} 个航班`,
            timestamp: Date.now(),
            changes,
        },
        warnings,
    };
}

/**
 * 构建一个编辑命令对应的 ChangeSet。
 *
 * @param {import("./editCommand").EditCommand} command 编辑命令。
 * @param {Map<string, Object>} localFlightsMap 本地航班工作副本。
 * @param {Object} referenceData 引用数据，如 routesById。
 * @param {Object} airportConstraintsMap 机场约束映射，用于 GT 派生。
 * @returns {{ command: Object, changeSet: Object, warnings: Array<string|Object> }} ChangeSet 构建结果。
 */
export function buildChangeSet(command, localFlightsMap, referenceData = {}, airportConstraintsMap = {}) {
    const { flightId, payload, type } = command;

    if (type === ADD_FLIGHT) {
        return buildAddFlightChangeSet(command, localFlightsMap);
    }

    if (type === BATCH_ADD_FLIGHTS) {
        return buildBatchAddFlightsChangeSet(command, localFlightsMap);
    }

    if (type === DELETE_FLIGHT) {
        return buildDeleteFlightChangeSet(command, localFlightsMap);
    }

    if (type === BATCH_DELETE_FLIGHTS) {
        return buildBatchDeleteFlightsChangeSet(command, localFlightsMap);
    }

    if (type === BATCH_UPDATE_FLIGHT_FIELDS) {
        return buildBatchUpdateFlightFieldsChangeSet(command, localFlightsMap, referenceData, airportConstraintsMap);
    }

    if (BACKEND_DRIVEN_TYPES.has(type)) {
        return buildBackendDrivenChangeSet(command, localFlightsMap, airportConstraintsMap);
    }

    if (FRONTEND_BATCH_TYPES.has(type)) {
        return buildFrontendBatchChangeSet(command, localFlightsMap, airportConstraintsMap);
    }

    const flightIdStr = String(flightId);
    const currentFlight = localFlightsMap.get(flightIdStr);
    if (!currentFlight) {
        return buildFlightNotFoundChangeSet(command);
    }

    const changes = [];
    const allWarnings = [];
    const allPatches = {};
    const effectivePayload = payload ? normalizeEditPayload(stripRouteDerivedPayloadFields(payload, currentFlight)) : payload;
    const userChangedFields = effectivePayload
        ? Object.keys(effectivePayload).filter((field) => !valuesEqual(field, currentFlight[field], effectivePayload[field]))
        : [];
    const fieldsToProcess = orderUserChangedFields(userChangedFields);
    const effectiveReferenceData = mergeCommandReferenceData(referenceData, command.meta?.referenceData || {});

    fieldsToProcess.forEach((field) => {
        const newValue = effectivePayload[field];
        const oldValue = currentFlight[field];

        changes.push({
            flightId: flightIdStr,
            field,
            before: oldValue ?? null,
            after: newValue ?? null,
        });

        const derivedFlight = {
            ...currentFlight,
            ...allPatches,
            ...effectivePayload,
            [field]: newValue,
        };
        const { patches, warnings } = applyRecalculationRules(field, {
            newValue,
            previousValue: oldValue,
            currentFlight: derivedFlight,
            referenceData: effectiveReferenceData,
        });

        Object.assign(allPatches, patches);
        allWarnings.push(...warnings);
    });

    Object.entries(allPatches).forEach(([field, afterValue]) => {
        const alreadyChanged = changes.some((change) => change.field === field);
        if (!alreadyChanged && afterValue !== undefined && !valuesEqual(field, currentFlight[field], afterValue)) {
            changes.push({
                flightId: flightIdStr,
                field,
                before: currentFlight[field] ?? null,
                after: afterValue ?? null,
            });
        }
    });

    const sourceAlreadyChanged = changes.some((change) => change.field === "change_source");
    if (userChangedFields.length > 0 && !sourceAlreadyChanged && !valuesEqual("change_source", currentFlight.change_source, USER_CHANGE_SOURCE)) {
        changes.push({
            flightId: flightIdStr,
            field: "change_source",
            before: currentFlight.change_source ?? null,
            after: USER_CHANGE_SOURCE,
        });
    }

    const fieldLabels = userChangedFields.join(", ");
    const flightNumber = currentFlight.flight_number || flightIdStr;

    return {
        command,
        changeSet: {
            id: buildChangeSetId(),
            type,
            label: `修改 ${flightNumber} ${fieldLabels}`,
            timestamp: Date.now(),
            changes: mergeCalculatedGroundTimeChanges(changes, localFlightsMap, airportConstraintsMap),
        },
        warnings: allWarnings,
    };
}

export default buildChangeSet;
